import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SidebarLayout from '../components/SidebarLayout';
import { useAgents } from '../hooks/useAgents';
import { ProviderLogo } from '../components/ui/ProviderLogo';
import { getAgentStatus, formatGoalName } from '../utils/agentStatus';
import { Search } from 'lucide-react';

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, green, loading }) {
  return (
    <div style={{
      background: '#0a0a0a',
      border: '1px solid rgba(255,255,255,0.06)',
      padding: '18px 20px',
      flex: 1,
      borderRight: 'none',
    }}>
      <div style={{
        fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
        fontSize: 9.5,
        fontWeight: 500,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: '#3f3f46',
        marginBottom: 8,
      }}>{label}</div>
      {loading ? (
        <div style={{ height: 32, background: 'rgba(255,255,255,0.04)', borderRadius: 4, marginBottom: 4 }} />
      ) : (
        <div style={{
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: '-0.04em',
          lineHeight: 1,
          marginBottom: 4,
          color: green ? '#22c55e' : '#fff',
        }}>{value}</div>
      )}
      {sub && <div style={{ fontSize: 11.5, color: '#3f3f46' }}>{sub}</div>}
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ state }) {
  const map = {
    optimized: { bg: 'rgba(34,197,94,0.08)', color: '#22c55e', dot: '#22c55e', label: 'Active' },
    stable:    { bg: 'rgba(34,197,94,0.08)', color: '#22c55e', dot: '#22c55e', label: 'Active' },
    learning:  { bg: 'rgba(34,197,94,0.08)', color: '#22c55e', dot: '#22c55e', label: 'Active' },
    waiting:   { bg: 'rgba(255,255,255,0.04)', color: '#3f3f46', dot: null, label: 'No data' },
  };
  const s = map[state] || map.waiting;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 4,
      fontFamily: "'Geist Mono', monospace", fontSize: 10.5, fontWeight: 500,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      {s.dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />}
      {s.label}
    </span>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ background: '#0a0a0a', padding: '18px 20px', borderRight: i < 4 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ height: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: '60%', marginBottom: 10 }} />
            <div style={{ height: 28, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: '80%' }} />
          </div>
        ))}
      </div>
      <div style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ height: 46, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 16 }}>
            <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 3, flex: 2 }} />
            <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: 60 }} />
            <div style={{ height: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 3, width: 80 }} />
          </div>
        ))}
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AgentsNew() {
  const { data, isLoading } = useAgents();
  const agents = data?.agents || [];

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('activity');

  // ── Compute global stats ──────────────────────────────────────────────────
  const totalDecisions = agents.reduce((sum, agent) =>
    sum + (agent.paths || []).reduce((s, p) => s + (p.sample_count || 0), 0), 0
  );

  const totalHeals = agents.reduce((sum, agent) =>
    sum + (agent.paths || []).reduce((s, p) => s + (p.failures || 0), 0), 0
  );

  let avgSuccessRate = 0;
  let totalSamples = 0;
  let totalWeightedSuccess = 0;
  agents.forEach(agent => {
    (agent.paths || []).forEach(path => {
      const samples = path.sample_count || 0;
      const rate = path.success_rate || 0;
      totalSamples += samples;
      totalWeightedSuccess += samples * rate;
    });
  });
  if (totalSamples > 0) {
    avgSuccessRate = (totalWeightedSuccess / totalSamples) * 100;
  }

  // Cost saved estimate: sum of (sample_count * (avg_gpt4o_cost - actual_avg_cost)) per path
  // Approximation: GPT-4o baseline ~$0.018/run, actual varies
  const totalCostSaved = agents.reduce((sum, agent) => {
    return sum + (agent.paths || []).reduce((s, p) => {
      const samples = p.sample_count || 0;
      const actualCost = p.avg_cost_usd || 0;
      const gpt4oBaseline = 0.018;
      const saved = Math.max(0, (gpt4oBaseline - actualCost) * samples);
      return s + saved;
    }, 0);
  }, 0);

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const processedAgents = useMemo(() => {
    let filtered = agents;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = agents.filter(a => (a.goal || '').toLowerCase().includes(q));
    }

    const getAgentSamples = (a) => (a.paths || []).reduce((s, p) => s + (p.sample_count || 0), 0);
    const getAgentSuccessRate = (a) => {
      const paths = a.paths || [];
      const samples = paths.reduce((s, p) => s + (p.sample_count || 0), 0);
      if (samples === 0) return 0;
      const weighted = paths.reduce((s, p) => s + (p.sample_count || 0) * (p.success_rate || 0), 0);
      return (weighted / samples) * 100;
    };

    return [...filtered].sort((a, b) => {
      if (sortBy === 'success') return getAgentSuccessRate(b) - getAgentSuccessRate(a);
      if (sortBy === 'decisions') return getAgentSamples(b) - getAgentSamples(a);
      return getAgentSamples(b) - getAgentSamples(a);
    });
  }, [agents, searchQuery, sortBy]);

  const mono = { fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" };

  return (
    <SidebarLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#000' }}>

        {/* Page header */}
        <div style={{
          height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 28px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: '#0a0a0a', flexShrink: 0,
        }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Agents</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ position: 'relative', display: 'inline-flex', width: 8, height: 8 }}>
              <span style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'rgba(34,197,94,0.4)',
                animation: 'ping 1.5s ease-out infinite',
              }} />
              <span style={{ position: 'absolute', inset: 1, borderRadius: '50%', background: '#22c55e' }} />
              <style>{`@keyframes ping{0%{transform:scale(1);opacity:.7}100%{transform:scale(2.2);opacity:0}}`}</style>
            </span>
            <span style={{ ...mono, fontSize: 11, color: '#3f3f46' }}>Live</span>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

          {isLoading && <LoadingSkeleton />}

          {!isLoading && agents.length === 0 && (
            <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: '#e4e4e7', marginBottom: 8 }}>
                Your agents will appear here
              </h2>
              <p style={{ ...mono, fontSize: 12, color: '#52525b', marginBottom: 24, lineHeight: 1.6 }}>
                Run any task through Kalibr to see it appear here automatically. No registration needed.
              </p>
              <div style={{
                background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8, padding: 20, textAlign: 'left',
              }}>
                <div style={{ ...mono, fontSize: 10, color: '#3f3f46', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                  Minimum setup
                </div>
                <pre style={{
                  ...mono, fontSize: 12, color: '#a1a1aa', lineHeight: 1.7,
                  margin: 0, whiteSpace: 'pre-wrap', overflowX: 'auto',
                }}>
{`pip install kalibr

import kalibr

response = kalibr.completion(
    goal="my_task",
    messages=[{"role": "user", "content": "Hello"}],
)`}
                </pre>
              </div>
              <a
                href="https://docs.kalibr.systems"
                target="_blank"
                rel="noreferrer"
                style={{ ...mono, fontSize: 12, color: '#52525b', textDecoration: 'none', display: 'inline-block', marginTop: 16 }}
              >
                View full documentation →
              </a>
            </div>
          )}

          {!isLoading && agents.length > 0 && (
            <>
              {/* KPI Strip */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(5,1fr)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, overflow: 'hidden', marginBottom: 20,
              }}>
                <KpiCard label="Total Agents" value={agents.length} />
                <KpiCard label="Total Decisions" value={totalDecisions.toLocaleString()} />
                <KpiCard
                  label="Heals by Kalibr ⚡"
                  value={totalHeals > 0 ? totalHeals.toLocaleString() : '—'}
                  sub="Automated interventions"
                  green={totalHeals > 0}
                />
                <KpiCard
                  label="Cost Saved by Kalibr"
                  value={totalCostSaved > 0 ? `$${totalCostSaved.toFixed(2)}` : '—'}
                  sub="vs. always GPT-4o"
                  green={totalCostSaved > 0}
                />
                <KpiCard
                  label="Avg Reliability"
                  value={`${avgSuccessRate.toFixed(1)}%`}
                  sub="Across all agents"
                  green={avgSuccessRate >= 75}
                />
              </div>

              {/* Search + sort */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 6, padding: '7px 12px', maxWidth: 280, flex: 1,
                }}>
                  <Search style={{ width: 13, height: 13, color: '#3f3f46', flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Filter agents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      background: 'none', border: 'none', outline: 'none',
                      color: '#fff', fontSize: 13, ...mono, width: '100%',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, overflow: 'hidden' }}>
                  {[
                    { key: 'activity', label: 'Activity' },
                    { key: 'success', label: 'Success' },
                    { key: 'decisions', label: 'Decisions' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setSortBy(key)}
                      style={{
                        padding: '6px 14px', ...mono, fontSize: 11,
                        background: sortBy === key ? 'rgba(255,255,255,0.06)' : 'none',
                        color: sortBy === key ? '#fff' : '#52525b',
                        border: 'none', cursor: 'pointer', transition: 'all .1s',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Table */}
              <div style={{
                background: '#0a0a0a',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8,
                overflow: 'hidden',
              }}>
                {/* Table header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>All Agents</div>
                  <span style={{ ...mono, fontSize: 11, color: '#3f3f46' }}>
                    {processedAgents.length} agent{processedAgents.length !== 1 ? 's' : ''} · click to drill in
                  </span>
                </div>

                {/* Column headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2.2fr 90px 100px 100px 180px 110px 90px',
                  padding: '9px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  {['Agent', 'Status', 'Decisions', 'Reliability', 'Leader Model', 'Mode', 'Health'].map((h, i) => (
                    <span key={h} style={{
                      ...mono, fontSize: 9.5, fontWeight: 500,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: '#3f3f46',
                      textAlign: i >= 2 ? 'right' : 'left',
                    }}>{h}</span>
                  ))}
                </div>

                {/* Agent rows */}
                {processedAgents.map((agent) => {
                  const status = getAgentStatus(agent);
                  const paths = agent.paths || [];
                  const agentSamples = paths.reduce((sum, p) => sum + (p.sample_count || 0), 0);

                  let agentSuccessRate = 0;
                  if (agentSamples > 0) {
                    const weightedSum = paths.reduce((sum, p) => sum + (p.sample_count || 0) * (p.success_rate || 0), 0);
                    agentSuccessRate = (weightedSum / agentSamples) * 100;
                  }

                  const leader = paths.find(p => p.status === 'leader');
                  const isExploiting = leader && leader.traffic_allocation > 0.5;
                  const mode = isExploiting ? 'Exploitation' : 'Exploration';

                  const reliabilityColor = agentSuccessRate >= 90 ? '#22c55e'
                    : agentSuccessRate >= 70 ? '#f59e0b'
                    : '#ef4444';

                  const isAttention = agentSuccessRate < 80 || status.state === 'waiting';

                  const statusBadge = status.state === 'optimized' || status.state === 'stable'
                    ? <span style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'2px 8px',borderRadius:4,...mono,fontSize:10.5,fontWeight:500,background:'rgba(34,197,94,0.10)',color:'#22c55e',whiteSpace:'nowrap' }}><span style={{width:5,height:5,borderRadius:'50%',background:'#22c55e'}}/>Healthy</span>
                    : <span style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'2px 8px',borderRadius:4,...mono,fontSize:10.5,fontWeight:500,background:'rgba(245,158,11,0.10)',color:'#f59e0b',whiteSpace:'nowrap' }}><span style={{width:5,height:5,borderRadius:'50%',background:'#f59e0b'}}/>Attention</span>;

                  return (
                    <Link
                      key={agent.goal}
                      to={`/agents/${encodeURIComponent(agent.goal)}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2.2fr 90px 100px 100px 180px 110px 90px',
                        padding: '0 16px',
                        height: 46,
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        borderLeft: isAttention ? '3px solid #f59e0b' : '3px solid transparent',
                        textDecoration: 'none',
                        transition: 'background 0.1s',
                        background: isAttention ? 'rgba(245,158,11,0.02)' : 'transparent',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isAttention ? 'rgba(245,158,11,0.02)' : 'transparent'; }}
                    >
                      {/* Agent name */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: isAttention ? 600 : 400, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formatGoalName(agent.goal)}
                        </div>
                        <div style={{ ...mono, fontSize: 10.5, color: '#3f3f46' }}>{agent.goal}</div>
                      </div>

                      {/* Status badge */}
                      <div><StatusBadge state={status.state} /></div>

                      {/* Decisions */}
                      <div style={{ ...mono, fontSize: 13, color: '#a1a1aa', textAlign: 'right' }}>
                        {agentSamples.toLocaleString()}
                      </div>

                      {/* Reliability */}
                      <div style={{ ...mono, fontSize: 13, fontWeight: 600, color: reliabilityColor, textAlign: 'right' }}>
                        {agentSuccessRate.toFixed(1)}%
                      </div>

                      {/* Leader model */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
                        {leader ? (
                          <>
                            <ProviderLogo provider={leader.model_id} size={14} />
                            <span style={{ ...mono, fontSize: 11, color: '#a1a1aa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {leader.model_id}
                            </span>
                          </>
                        ) : (
                          <span style={{ ...mono, fontSize: 11, color: '#3f3f46' }}>—</span>
                        )}
                      </div>

                      {/* Mode */}
                      <div style={{
                        ...mono, fontSize: 11, textAlign: 'right',
                        color: mode === 'Exploitation' ? '#22c55e' : '#60a5fa',
                      }}>
                        {mode}
                      </div>

                      {/* Health badge */}
                      <div style={{ textAlign: 'right' }}>{statusBadge}</div>
                    </Link>
                  );
                })}

                {processedAgents.length === 0 && (
                  <div style={{ padding: '40px 20px', textAlign: 'center', ...mono, fontSize: 12, color: '#52525b' }}>
                    {searchQuery ? `No agents match "${searchQuery}"` : 'No agents found. Add Kalibr to your code to see agents here.'}
                  </div>
                )}
              </div>

              {/* Empty state hint when no search */}
              {agents.length === 0 && !isLoading && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <a
                    href="https://docs.kalibr.systems"
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...mono, fontSize: 12, color: '#52525b', textDecoration: 'none' }}
                  >
                    Don't see your agent? Add Kalibr to your code →
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
}
